import React, { useState, useEffect, useMemo } from 'react';
import { Settings, Search, Play, Trophy, Zap, Star, BarChart3, TrendingDown, BookOpen, ExternalLink, Share, Share2, MessageCircle, Mail, Shield, ThumbsUp, X, Palette, Type, FileText, Target, AlertTriangle, Lightbulb, Bookmark, Clock, Heart, Clock3, CheckCircle2, XCircle, Home, HelpCircle, MoreVertical, Flame, HeartPulse, Scale, Target as TargetIcon, Wrench, ChevronRight } from 'lucide-react';
import { useApp } from './AppContext';
import { Footer } from './Footer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, AreaChart, Area, PieChart, Pie } from 'recharts';
import ShareModal from './ShareModal';
import shieldCheckImage320 from '../assets/shield-320w.png';
import shieldCheckImage640 from '../assets/shield-640w.png';
import shieldCheckImage966 from '../assets/shield-966w.png';
import type { Screen } from '../App';
import type { Question } from './AppContext';
import { YandexRTB } from './YandexRTB';

// Extend the Window interface to include Yandex RTB properties
declare global {
  interface Window {
    yaContextCb?: Array<() => void>;
    Ya?: {
      Context?: {
        AdvManager?: {
          render: (config: { blockId: string; type?: string; platform?: string; renderTo?: string }) => void;
        };
      };
    };
  }
}

interface MainScreenProps {
  onNavigate: (screen: Screen) => void;
}

interface Tip {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  benefit: string;
  isRuStorePromo?: boolean; // Special flag for RuStore promotion
  isGooglePlayPromo?: boolean; // Special flag for Google Play promotion
}

const TIPS: Tip[] = [
  // Советы из настроек
  {
    icon: Palette,
    title: 'Выбор темы и фона',
    description: 'Настройте внешний вид приложения под себя',
    benefit: 'Создайте комфортную визуальную среду для обучения, которая не будет утомлять глаза'
  },
  {
    icon: Type,
    title: 'Размер шрифта вопросов',
    description: 'Увеличьте или уменьшите размер текста вопросов',
    benefit: 'Адаптируйте интерфейс под ваше зрение для более удобного чтения и лучшего восприятия информации'
  },
  {
    icon: FileText,
    title: 'Размер шрифта документов',
    description: 'Изменяйте размер текста в документах',
    benefit: 'Читайте нормативные документы с комфортом, настраивая размер под ваши предпочтения'
  },
  // Советы из подготовки
  {
    icon: Star,
    title: 'Используйте избранное',
    description: 'Добавляйте сложные вопросы в избранное для последующего повторения',
    benefit: 'Создайте персональную подборку важных вопросов и быстро возвращайтесь к ним для закрепления материала'
  },
  {
    icon: Lightbulb,
    title: 'Читайте пояснения',
    description: 'Обязательно читайте пояснения к ответам после прохождения теста',
    benefit: 'Пояснения помогают понять логику правильного ответа и лучше запомнить материал'
  },
  {
    icon: Target,
    title: 'Изучайте категории поочередно',
    description: 'Проходите категории последовательно для лучшего запоминания',
    benefit: 'Систематическое изучение помогает структурировать знания и улучшает долгосрочное запоминание'
  },
  // Советы из экзамена
  {
    icon: Trophy,
    title: 'Подготовка к экзамену',
    description: 'Изучите все категории в разделе "Подготовка" перед экзаменом',
    benefit: 'Хорошая подготовка повышает шансы на успешную сдачу экзамена с минимальным количеством ошибок'
  },
  {
    icon: AlertTriangle,
    title: 'Работайте с топом ошибок',
    description: 'Проработайте вопросы из раздела "Топ ошибок" перед экзаменом',
    benefit: 'Фокус на проблемных вопросах помогает устранить пробелы в знаниях и повысить общую успеваемость'
  },
  // Советы из марафона
  {
    icon: Zap,
    title: 'Создавайте марафоны',
    description: 'Создавайте персонализированные наборы вопросов для целенаправленной подготовки',
    benefit: 'Марафоны позволяют комбинировать категории, избранное и проблемные вопросы для эффективного обучения'
  },
  // Советы из статистики
  {
    icon: BarChart3,
    title: 'Отслеживайте прогресс',
    description: 'Регулярно проверяйте статистику для оценки своего прогресса',
    benefit: 'Анализ статистики помогает выявить слабые места и скорректировать план обучения'
  },
  {
    icon: TrendingDown,
    title: 'Определяйте слабые категории',
    description: 'Обращайте внимание на категории с низким процентом правильных ответов',
    benefit: 'Фокус на проблемных темах позволяет эффективно распределять время и улучшать результаты'
  },
  // Советы из избранного
  {
    icon: Heart,
    title: 'Периодически повторяйте избранное',
    description: 'Регулярно проверяйте избранные вопросы для закрепления знаний',
    benefit: 'Повторение важных вопросов укрепляет память и повышает уверенность в знаниях'
  },
  // Советы из документов
  {
    icon: Bookmark,
    title: 'Создавайте закладки в документах',
    description: 'Используйте закладки для быстрого доступа к важным местам в документах',
    benefit: 'Закладки помогают быстро находить нужную информацию и эффективно работать с большими документами'
  },
  {
    icon: Clock,
    title: 'Прогресс чтения сохраняется',
    description: 'Система автоматически запоминает вашу позицию в документах',
    benefit: 'Вы всегда можете вернуться к месту, где остановились, и продолжить чтение без поиска'
  },
  // RuStore Promotion
  {
    icon: Shield, // Using Shield as icon, but will be overridden in rendering
    title: 'Скачайте приложение из RuStore',
    description: 'Установите приложение ВОХР ТЕСТ из официального магазина RuStore',
    benefit: 'Получите доступ к тестам в отсутствие интернета и наслаждайтесь улучшенной производительностью',
    isRuStorePromo: true
  },
  // Google Play Promotion
  {
    icon: Shield, // Using Shield as icon, but will be overridden in rendering
    title: 'Скачайте приложение из Google Play',
    description: 'Установите приложение ВОХР ТЕСТ из официального магазина Google Play',
    benefit: 'Получите доступ к тестам в отсутствие интернета и наслаждайтесь улучшенной производительностью',
    isGooglePlayPromo: true
  }
];

// Mock data for the chart
const ACTIVITY_DATA = [
  { name: 'Пн', score: 45 },
  { name: 'Вт', score: 60 },
  { name: 'Ср', score: 55 },
  { name: 'Чт', score: 80 },
  { name: 'Пт', score: 70 },
  { name: 'Сб', score: 90 },
  { name: 'Вс', score: 85 },
];

const SEO_INTENTS = [
  {
    icon: FileText,
    title: 'Билеты ведомственной охраны 2026',
    text: '235 вопросов с ответами. Вопросы актуальны с 01.09.2026. Экзамен, марафон, тесты с оружием, разделы по огневой, правовой, тактической подготовке, первой помощи и спецсредствам.',
    href: '/preparation',
    screen: 'preparation' as Screen,
  },
  {
    icon: TargetIcon,
    title: 'Тесты ВОХР 2026 онлайн',
    text: 'Тренируйтесь по категориям, проходите марафон и запускайте экзамен в условиях, приближенных к реальной проверке знаний.',
    href: '/exam',
    screen: 'exam' as Screen,
  },
  {
    icon: Shield,
    title: 'Билеты с оружием',
    text: 'Отдельные вопросы по применению оружия, специальным средствам, правовым основаниям и безопасному порядку действий охранника.',
    href: '/documents/weapons-law',
    screen: 'doc-weapons' as Screen,
  },
  {
    icon: Scale,
    title: 'Периодическая проверка',
    text: 'Нормативная база, порядок проверки, упражнения и тесты для сотрудников ведомственной охраны перед сдачей экзамена.',
    href: '/documents/periodic-check',
    screen: 'doc-periodic-check' as Screen,
  },
];

const SEO_FAQ = [
  {
    question: 'Какие билеты нужны для ведомственной охраны с оружием?',
    answer: 'Используйте вопросы по огневой подготовке, правовой подготовке, тактико-специальной подготовке, первой помощи и специальным средствам. ВОХР ТЕСТ объединяет эти темы в базе из 235 вопросов с ответами.',
  },
  {
    question: 'Есть ли тесты ВОХР на 2026 год?',
    answer: 'Да, на сайте доступны 235 вопросов с ответами, актуальные с 01.09.2026: билеты ведомственной охраны, режим подготовки, онлайн-экзамен, марафон и поиск по вопросам.',
  },
  {
    question: 'Как готовиться к периодической проверке ведомственной охраны онлайн?',
    answer: 'Начните с подготовки по категориям, затем разберите ошибки, повторите сложные вопросы и проверьте результат в режиме экзамена.',
  },
];

const SimpleAreaChart = ({ data }: { data: typeof ACTIVITY_DATA }) => {
  const maxValue = 100;
  const width = 100;
  const height = 40;

  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (d.score / maxValue) * height;
    return `${x},${y}`;
  }).join(' ');

  const firstPoint = points.split(' ')[0];
  const lastPoint = points.split(' ')[points.split(' ').length - 1];

  const areaPath = `M ${firstPoint} L ${points.split(' ').slice(1).join(' L ')} L ${width},${height} L 0,${height} Z`;
  const linePath = `M ${firstPoint} L ${points.split(' ').slice(1).join(' L ')}`;

  return (
    <div className="w-full h-full flex items-end justify-between relative pb-6">
      {/* Grid lines */}
      <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-6">
        <div className="border-t border-white/10 w-full h-0"></div>
        <div className="border-t border-white/10 w-full h-0"></div>
        <div className="border-t border-white/10 w-full h-0"></div>
        <div className="border-t border-white/10 w-full h-0"></div>
      </div>

      {/* SVG Chart */}
      <div className="absolute inset-0 pb-6">
        <svg className="w-full h-full overflow-visible" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#chartGradient)" />
          <path d={linePath} fill="none" stroke="#60a5fa" strokeWidth="1" vectorEffect="non-scaling-stroke" />

          {/* Points */}
          {data.map((d, i) => {
            const x = (i / (data.length - 1)) * width;
            const y = height - (d.score / maxValue) * height;
            return (
              <circle key={i} cx={x} cy={y} r="2" fill="#1e293b" stroke="#60a5fa" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            );
          })}
        </svg>
      </div>

      {/* X Axis Labels */}
      {data.map((d, i) => (
        <div key={i} className="text-[10px] text-white/70 relative z-10 translate-y-1">{d.name}</div>
      ))}
    </div>
  );
};

const SeoLandingSection = ({ onNavigate }: MainScreenProps) => (
  <section className="space-y-5" aria-labelledby="seo-preparation-title">
    <div className="space-y-2">
      <h2 id="seo-preparation-title" className="text-lg sm:text-xl md:text-2xl font-semibold glass-text">
        Билеты и тесты ведомственной охраны 2026
      </h2>
      <p className="text-sm sm:text-base glass-text-secondary max-w-3xl leading-relaxed">
        ВОХР ТЕСТ помогает готовиться к периодической проверке ведомственной охраны онлайн: тренировка по билетам,
        экзамен, марафон, тесты с оружием и быстрый поиск по вопросам.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {SEO_INTENTS.map((item) => {
        const Icon = item.icon;
        return (
          <a
            key={item.title}
            href={item.href}
            onClick={(e) => {
              e.preventDefault();
              onNavigate(item.screen);
            }}
            className="glass-panel-interactive rounded-xl p-4 sm:p-5 flex gap-4 glass-text-secondary"
          >
            <Icon className="w-5 h-5 glass-icon flex-shrink-0 mt-0.5" />
            <span className="min-w-0">
              <span className="block font-semibold glass-text mb-1">{item.title}</span>
              <span className="block text-xs sm:text-sm leading-relaxed">{item.text}</span>
            </span>
          </a>
        );
      })}
    </div>

    <div className="space-y-3" aria-labelledby="seo-faq-title">
      <h2 id="seo-faq-title" className="text-base sm:text-lg font-semibold glass-text">
        Частые вопросы по подготовке ВОХР
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {SEO_FAQ.map((item) => (
          <article key={item.question} className="glass-panel rounded-xl p-4">
            <h3 className="text-sm font-semibold glass-text mb-2 leading-snug">{item.question}</h3>
            <p className="text-xs sm:text-sm glass-text-secondary leading-relaxed">{item.answer}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export const MainScreen: React.FC<MainScreenProps> = ({ onNavigate }) => {
  const { userStats, testSession, marathons, endTest, getBackgroundClass, categories, questions } = useApp();
  const [isRemovingTest, setIsRemovingTest] = useState(false);
  const [isSettingsInfoClosed, setIsSettingsInfoClosed] = useState(false);
  const [isRemovingSettingsInfo, setIsRemovingSettingsInfo] = useState(false);
  const [currentTip, setCurrentTip] = useState<Tip | null>(null);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  // Stats calculation
  const totalQuestionsAnswered = userStats.totalAnswered;
  const successRate = totalQuestionsAnswered > 0
    ? Math.round((userStats.correctAnswers / totalQuestionsAnswered) * 100)
    : 0;
  const totalTimeMinutes = userStats.studyTimeMinutes + userStats.examTimeMinutes + userStats.marathonTimeMinutes;
  const hours = Math.floor(totalTimeMinutes / 60);
  const minutes = totalTimeMinutes % 60;

  // Проверка при монтировании компонента
  useEffect(() => {
    console.log('✅ MainScreen смонтирован');
  }, []);

  // Инициализация совета при загрузке - один раз за сессию браузера
  useEffect(() => {
    try {
      // Проверяем, был ли уже показан совет в этой сессии
      const tipShown = sessionStorage.getItem('testApp-settingsTipShown');
      const tipClosed = sessionStorage.getItem('testApp-settingsInfoClosed');

      if (tipShown === 'true' || tipClosed === 'true') {
        // Совет уже был показан или закрыт в этой сессии - не показываем
        setIsSettingsInfoClosed(true);
      } else {
        // Выбираем случайный совет и сохраняем в sessionStorage
        const randomTip = TIPS[Math.floor(Math.random() * TIPS.length)];
        setCurrentTip(randomTip);
        sessionStorage.setItem('testApp-settingsTipShown', 'true');
      }
    } catch (error) {
      console.error('Error initializing settings tip:', error);
      // В случае ошибки выбираем совет, но не сохраняем
      const randomTip = TIPS[Math.floor(Math.random() * TIPS.length)];
      setCurrentTip(randomTip);
    }
  }, []);

  // Очищаем тестовую сессию, если связанный марафон больше не существует
  useEffect(() => {
    if (testSession &&
      testSession.type === 'marathon' &&
      testSession.marathonId &&
      !marathons.some(m => m.id === testSession.marathonId)) {
      console.log('⚠️ Marathon not found, clearing test session:', testSession.marathonId);
      endTest();
    }
  }, [testSession, marathons, endTest]);

  const continueTest = () => {
    if (testSession && !isRemovingTest) {
      onNavigate('test');
    }
  };

  const handleRemoveTest = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRemovingTest(true);

    setTimeout(() => {
      endTest();
      setIsRemovingTest(false);
    }, 300);
  };



  const handleCloseSettingsInfo = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsRemovingSettingsInfo(true);

    setTimeout(() => {
      setIsSettingsInfoClosed(true);
      setIsRemovingSettingsInfo(false);
      try {
        // Сохраняем в sessionStorage, чтобы блок не появлялся в текущей сессии
        sessionStorage.setItem('testApp-settingsInfoClosed', 'true');
      } catch (error) {
        console.error('Error saving settings info closed state:', error);
      }
    }, 300);
  };

  const handleOpenTip = () => {

    // Определяем, куда ведет совет, и переходим туда
    if (currentTip) {
      const tipTitle = currentTip.title.toLowerCase();
      if (tipTitle.includes('настройк') || tipTitle.includes('шрифт') || tipTitle.includes('тема') || tipTitle.includes('фон')) {
        onNavigate('settings');
      } else if (tipTitle.includes('подготовк') || tipTitle.includes('категори')) {
        onNavigate('preparation');
      } else if (tipTitle.includes('экзамен')) {
        onNavigate('exam');
      } else if (tipTitle.includes('марафон')) {
        onNavigate('marathon');
      } else if (tipTitle.includes('избранн') || tipTitle.includes('звезд')) {
        onNavigate('favorites');
      } else if (tipTitle.includes('статистик') || tipTitle.includes('прогресс')) {
        onNavigate('statistics');
      } else if (tipTitle.includes('ошибок') || tipTitle.includes('топ')) {
        onNavigate('error-top');
      } else if (tipTitle.includes('документ') || tipTitle.includes('закладк')) {
        onNavigate('documents-catalog');
      } else {
        // По умолчанию открываем настройки
        onNavigate('settings');
      }
    }
  };

  const handleContactSupport = () => {
    setShowContactDialog(true);
  };

  const handleContactTelegram = () => {
    const botUsername = 'BOXP_AIbot';
    const telegramWebUrl = `https://t.me/${botUsername}`;
    window.open(telegramWebUrl, '_blank');
    setShowContactDialog(false);
  };

  const handleContactEmail = () => {
    window.location.href = 'mailto:bumer.bmw@gmail.com';
    setShowContactDialog(false);
  };

  const toggleShareModal = () => {
    setIsShareModalOpen(!isShareModalOpen);
  };

  const handleShare = async () => {
    // Fallback: веб-версия для браузера
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;

    const rustoreUrl = 'https://apps.rustore.ru/app/com.pp.app';
    const webUrl = window.location.origin; // Веб-версия приложения

    const shareTitle = 'ВОХР ТЕСТ';
    const shareText = 'Попробуйте приложение для тестирования сотрудников ведомственной охраны';

    // Выбираем ссылку в зависимости от платформы
    let shareUrl = rustoreUrl;
    if (!isMobile) {
      shareUrl = webUrl;
    }

    // Для мобильных используем Web Share API
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('❌ Ошибка при попытке поделиться:', error);
        }
      }
    } else {
      // Для десктопа открываем email клиент
      const subject = encodeURIComponent(shareTitle);
      const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
  };

  const handleRate = () => {
    // Fallback: веб-версия для браузера
    const rustoreUrl = 'https://apps.rustore.ru/app/com.pp.app';
    window.open(rustoreUrl, '_blank');
  };

  // --- Components for cleaner render ---

  const InterruptedTestCard = () => (
    testSession && !testSession.isCompleted &&
      (testSession.type !== 'marathon' || (testSession.marathonId && marathons.some(m => m.id === testSession.marathonId))) ? (
      <div className={`w-full mb-6 sm:mb-8 md:mb-10 lg:mb-0 glass-panel rounded-2xl shadow-lg relative ${isRemovingTest ? 'item-removing' : ''} h-full`}>
        <button
          onClick={continueTest}
          className="w-full h-full glass-interactive rounded-2xl flex flex-col justify-between"
          style={{ transition: 'none' }}
          disabled={isRemovingTest}
        >
          <div className="p-6 sm:p-8 md:p-10 lg:p-8 w-full">
            <div className="text-left">
              <div className="flex items-center gap-2 mb-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </span>
                <h2 className="font-semibold text-sm sm:text-lg md:text-xl lg:text-xl glass-text leading-tight">Прерванный тест</h2>
              </div>
              <p className="text-xs sm:text-sm md:text-base lg:text-base glass-text-secondary mb-3 leading-tight">
                {testSession.type === 'preparation' && 'Подготовка'}
                {testSession.type === 'exam' && 'Экзамен'}
                {testSession.type === 'marathon' && (() => {
                  const currentMarathon = marathons.find(m => m.id === testSession.marathonId);
                  return currentMarathon ? `Марафон "${currentMarathon.name}"` : 'Марафон';
                })()}
                {testSession.category && ` - ${testSession.category}`}
              </p>
              <p className="text-xs sm:text-sm md:text-base lg:text-base glass-text-secondary mb-4 leading-tight">
                Пройдено {testSession.currentQuestionIndex} из {testSession.questions.length} вопросов
              </p>

              {/* Progress Bar */}
              <div className="w-full glass-progress-bg rounded-full h-3 sm:h-4 lg:h-3 overflow-hidden">
                <div
                  className="glass-progress-fill h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${((testSession.currentQuestionIndex) / testSession.questions.length) * 100}%`,
                    backgroundColor: '#34d399'
                  }}
                ></div>
              </div>
            </div>
          </div>
          <div className="p-6 lg:p-8 pt-0 w-full">
            <div className="w-full py-3 bg-white/10 rounded-xl text-center text-sm font-medium glass-text group-hover:bg-white/20 transition-colors">
              Продолжить
            </div>
          </div>
        </button>

        {/* Close Button */}
        <button
          onClick={handleRemoveTest}
          className="absolute top-4 right-4 arrow-button-sm"
          style={{ transition: 'none' }}
          disabled={isRemovingTest}
          aria-label="Удалить прерванный тест"
        >
          <X className="arrow-icon-sm" />
        </button>
      </div>
    ) : null
  );

  const TipCard = () => (
    (!testSession || testSession.isCompleted) && !isSettingsInfoClosed && currentTip ? (
      <div className={`w-full mb-6 sm:mb-8 md:mb-10 lg:mb-0 glass-panel rounded-2xl shadow-lg relative ${isRemovingSettingsInfo ? 'item-removing' : ''} h-full`}>
        {currentTip.isRuStorePromo ? (
          // Special rendering for RuStore promotion
          <div className="w-full h-full glass-interactive rounded-2xl flex flex-col justify-center items-center p-6 sm:p-8 md:p-10 lg:p-8">
            <a
              href="https://www.rustore.ru/catalog/app/com.pp.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-4 transition-transform hover:scale-105"
              style={{ cursor: 'pointer' }}
            >
              <img
                src="/rustore-logo.png"
                alt="Скачайте из RuStore"
                className="h-12 sm:h-14 md:h-16"
                style={{ objectFit: 'contain', maxWidth: '200px' }}
              />
              <div className="text-center">
                <p className="text-xs sm:text-sm md:text-base lg:text-sm glass-text-secondary mb-3 leading-tight">
                  {currentTip.description}
                </p>
                <div className="glass-panel rounded-lg p-3 sm:p-4 md:p-4 mt-2">
                  <p className="text-xs sm:text-sm md:text-base lg:text-sm glass-text leading-tight">
                    <span className="font-semibold">Зачем это нужно:</span> {currentTip.benefit}
                  </p>
                </div>
              </div>
            </a>
          </div>
        ) : currentTip.isGooglePlayPromo ? (
          // Special rendering for Google Play promotion
          <div className="w-full h-full glass-interactive rounded-2xl flex flex-col justify-center items-center p-6 sm:p-8 md:p-10 lg:p-8">
            <a
              href="https://play.google.com/store/apps/details?id=ru.vohr.pp&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-4 transition-transform hover:scale-105"
              style={{ cursor: 'pointer' }}
            >
              <img
                src="/google-play-logo.png"
                alt="Доступно в Google Play"
                className="h-12 sm:h-14 md:h-16"
                style={{ objectFit: 'contain', maxWidth: '220px' }}
              />
              <div className="text-center">
                <p className="text-xs sm:text-sm md:text-base lg:text-sm glass-text-secondary mb-3 leading-tight">
                  {currentTip.description}
                </p>
                <div className="glass-panel rounded-lg p-3 sm:p-4 md:p-4 mt-2">
                  <p className="text-xs sm:text-sm md:text-base lg:text-sm glass-text leading-tight">
                    <span className="font-semibold">Зачем это нужно:</span> {currentTip.benefit}
                  </p>
                </div>
              </div>
            </a>
          </div>
        ) : (
          // Regular tip rendering
          <button
            onClick={handleOpenTip}
            className="w-full h-full glass-interactive rounded-2xl flex flex-col"
            style={{ transition: 'none' }}
            disabled={isRemovingSettingsInfo}
          >
            <div className="p-6 sm:p-8 md:p-10 lg:p-8 w-full text-left flex-1">
              <h2 className="font-semibold mb-3 text-sm sm:text-lg md:text-xl lg:text-xl glass-text leading-tight flex items-center gap-2">
                {React.createElement(currentTip.icon, { className: "h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 lg:h-6 lg:w-6 glass-icon" })}
                {currentTip.title}
              </h2>
              <p className="text-xs sm:text-sm md:text-base lg:text-sm glass-text-secondary mb-3 leading-tight">
                {currentTip.description}
              </p>
              <div className="glass-panel rounded-lg p-3 sm:p-4 md:p-4 mt-2">
                <p className="text-xs sm:text-sm md:text-base lg:text-sm glass-text leading-tight">
                  <span className="font-semibold">Зачем это нужно:</span> {currentTip.benefit}
                </p>
              </div>
            </div>
          </button>
        )}

        {/* Close Button */}
        <button
          onClick={handleCloseSettingsInfo}
          className="absolute top-4 right-4 arrow-button-sm"
          style={{ transition: 'none' }}
          disabled={isRemovingSettingsInfo}
          aria-label="Закрыть подсказку"
        >
          <X className="arrow-icon-sm" />
        </button>
      </div>
    ) : null
  );

  // Получаем статистику по категориям для круговой диаграммы
  const categoryStatsData = useMemo(() => {
    return categories.map(category => {
      const categoryQuestions = questions.filter(q => q.category === category);
      const stats = userStats.categoryStats[category] || { correct: 0, incorrect: 0, answeredQuestions: [] };
      const total = stats.correct + stats.incorrect;

      // Процент прогресса (как в экране подготовки): сколько вопросов отвечено из всех вопросов в категории
      const answeredQuestions = stats.answeredQuestions || [];
      const answered = answeredQuestions.length;
      const validAnswered = Math.min(answered, categoryQuestions.length);
      const percentage = categoryQuestions.length > 0 ? Math.round((validAnswered / categoryQuestions.length) * 100) : 0;

      // Иконки для категорий
      const categoryIcons: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
        'Огневая подготовка.': Flame,
        'Оказание первой помощи.': HeartPulse,
        'Правовая подготовка.': Scale,
        'Тактико-специальная подготовка.': TargetIcon,
        'Использование специальных средств.': Wrench,
      };

      const Icon = categoryIcons[category] || BookOpen;

      // Цвета для категорий (соответствуют изображению)
      const categoryColors: Record<string, string> = {
        'Огневая подготовка.': '#60a5fa', // синий (Часть 01)
        'Оказание первой помощи.': '#2563eb', // темно-синий (Часть 05)
        'Правовая подготовка.': '#fb7185', // розовый (Часть 04)
        'Тактико-специальная подготовка.': '#34d399', // зеленый (Часть 03)
        'Использование специальных средств.': '#fbbf24', // желтый (Часть 02)
      };

      return {
        category,
        total,
        correct: stats.correct,
        incorrect: stats.incorrect,
        percentage,
        Icon,
        color: categoryColors[category] || '#60a5fa',
        totalQuestions: categoryQuestions.length,
        answeredCount: validAnswered // Количество отвеченных вопросов
      };
    }); // Показываем все категории
  }, [categories, questions, userStats.categoryStats]);

  // Вычисляем общий процент прогресса на основе статистики категорий из подготовки
  // (аналогично тому, как это показывается в экране подготовки)
  const totalSuccessRate = useMemo(() => {
    let totalAnswered = 0;
    let totalQuestions = 0;

    categories.forEach(category => {
      const categoryQuestions = questions.filter(q => q.category === category);
      const stats = userStats.categoryStats[category] || { correct: 0, incorrect: 0, answeredQuestions: [] };

      // Используем answeredQuestions для подсчета уникальных отвеченных вопросов
      const answeredQuestions = stats.answeredQuestions || [];
      const answered = answeredQuestions.length;
      const validAnswered = Math.min(answered, categoryQuestions.length);

      totalAnswered += validAnswered;
      totalQuestions += categoryQuestions.length;
    });

    return totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
  }, [categories, questions, userStats.categoryStats]);

  // Функция для создания SVG path сектора круга
  const createArc = (startAngle: number, endAngle: number, radius: number, centerX: number, centerY: number) => {
    const start = {
      x: centerX + radius * Math.cos((startAngle - 90) * (Math.PI / 180)),
      y: centerY + radius * Math.sin((startAngle - 90) * (Math.PI / 180))
    };
    const end = {
      x: centerX + radius * Math.cos((endAngle - 90) * (Math.PI / 180)),
      y: centerY + radius * Math.sin((endAngle - 90) * (Math.PI / 180))
    };
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${centerX} ${centerY} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
  };

  // Функция для преобразования полярных координат в декартовы
  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  // Функция для создания треугольного лепестка (wedge)
  const describeTriangularSlice = (
    centerX: number,
    centerY: number,
    outerRadius: number,
    innerRadius: number,
    startAngle: number,
    endAngle: number
  ): string => {
    const startOuter = polarToCartesian(centerX, centerY, outerRadius, startAngle);
    const endOuter = polarToCartesian(centerX, centerY, outerRadius, endAngle);
    const center = { x: centerX, y: centerY };

    // Создаем треугольный путь: от центра к внешней точке начала, по дуге к внешней точке конца, обратно к центру
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    return `M ${center.x} ${center.y} 
            L ${startOuter.x} ${startOuter.y} 
            A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endOuter.x} ${endOuter.y} 
            Z`;
  };

  // Проверяем, является ли это desktop экраном
  // Инициализируем с проверкой размера экрана сразу, чтобы избежать мигания
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return false;
  });

  useEffect(() => {
    const checkDesktop = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
    };
    // Проверяем сразу при монтировании
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // Debug: проверяем данные
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
  const isWideScreen = windowWidth >= 1024;

  console.log('Desktop check:', {
    categories: categories?.length,
    questions: questions?.length,
    categoryStatsData: categoryStatsData?.length,
    windowWidth,
    isWideScreen
  });

  return (
    <div className={`h-full flex-1 min-h-0 w-full ${getBackgroundClass()} max-w-full flex flex-col relative`} style={{ margin: 0, padding: 0 }}>

      {/* DESKTOP DASHBOARD LAYOUT - показывается только на экранах >= 1024px */}
      {isWideScreen ? (
        <div className="w-full h-full flex-1 overflow-hidden flex" style={{ margin: 0, padding: 0 }}>
          {/* Sidebar */}
          <div className="w-72 glass-panel border-r flex flex-col p-6 gap-8">
            <div className="flex items-center gap-3 px-2">
              <div className="p-2 glass-panel-interactive shadow-lg flex items-center justify-center" style={{ borderRadius: '12px', width: '48px', height: '48px' }}>
                <img
                  src={shieldCheckImage320}
                  srcSet={`${shieldCheckImage320} 320w, ${shieldCheckImage640} 640w, ${shieldCheckImage966} 966w`}
                  sizes="32px"
                  alt="ВОХР ТЕСТ"
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h1 className="font-bold text-xl tracking-tight glass-text">ВОХР ТЕСТ</h1>
              </div>
            </div>

            <nav className="flex-1 flex flex-col gap-2" style={{ marginTop: '32px' }}>
              <p className="text-xs font-semibold glass-text-muted uppercase tracking-wider px-2 mb-2">Меню</p>

              <a href="/" onClick={(e) => { e.preventDefault(); onNavigate('main'); }} className="flex items-center gap-3 p-3 glass-panel-interactive font-medium glass-text-secondary" style={{ transition: 'none', borderRadius: '12px' }}>
                <Home className="w-5 h-5" />
                Главная
              </a>

              <a href="/preparation" onClick={(e) => { e.preventDefault(); onNavigate('preparation'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <Play className="w-5 h-5" />
                Подготовка
              </a>

              <a href="/exam" onClick={(e) => { e.preventDefault(); onNavigate('exam'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <Trophy className="w-5 h-5" />
                Экзамен
              </a>

              <a href="/marathon" onClick={(e) => { e.preventDefault(); onNavigate('marathon'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <Zap className="w-5 h-5" />
                Марафон
              </a>

              <a href="/favorites" onClick={(e) => { e.preventDefault(); onNavigate('favorites'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <Star className="w-5 h-5" />
                Избранное
              </a>

              <p className="text-xs font-semibold glass-text-muted uppercase tracking-wider px-2 mb-2 mt-6">Аналитика</p>

              <a href="/statistics" onClick={(e) => { e.preventDefault(); onNavigate('statistics'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <BarChart3 className="w-5 h-5" />
                Статистика
              </a>

              <a href="/error-top" onClick={(e) => { e.preventDefault(); onNavigate('error-top'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <TrendingDown className="w-5 h-5" />
                Ошибки
              </a>

              <p className="text-xs font-semibold glass-text-muted uppercase tracking-wider px-2 mb-2 mt-6">Дополнительно</p>

              <a href="/documents" onClick={(e) => { e.preventDefault(); onNavigate('documents-catalog'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <BookOpen className="w-5 h-5" />
                Полезная информация
              </a>

              <a href="/links" onClick={(e) => { e.preventDefault(); onNavigate('useful-links-page'); }} className="flex items-center gap-3 p-3 glass-text-secondary glass-panel-interactive font-medium" style={{ transition: 'none', borderRadius: '12px' }}>
                <ExternalLink className="w-5 h-5" />
                Полезные ссылки
              </a>
            </nav>

            <div className="p-4 glass-panel relative overflow-hidden cursor-pointer" onClick={() => onNavigate('settings')} style={{ borderRadius: '16px' }}>
              <div className="absolute top-0 right-0 w-24 h-24 bg-gray-400/20 blur-2xl rounded-full -mr-10 -mt-10"></div>
              <div className="flex items-center gap-3 relative z-10">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-gray-400 to-gray-600 flex items-center justify-center glass-text-secondary">
                  <Settings className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm glass-text-secondary truncate">Настройки</p>
                  <p className="text-xs glass-text-secondary truncate">Приложение и данные</p>
                </div>
                <ChevronRight className="w-4 h-4 glass-text-secondary" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative" style={{ margin: 0, padding: 0 }}>
            {/* Top Bar */}
            <header className="h-20 border-b border-white/20 flex items-center justify-between glass-panel backdrop-blur-sm" style={{ margin: 0, paddingTop: 0, paddingBottom: 0, paddingLeft: '40px', paddingRight: '32px' }}>
              <div></div>

              <div className="flex items-center gap-4">
                <a href="/search"
                  onClick={(e) => { e.preventDefault(); onNavigate('search'); }}
                  className="rounded-xl glass-panel-interactive flex items-center justify-center glass-text-secondary cursor-pointer flex-shrink-0"
                  style={{ transition: 'none', width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}
                  title="Поиск"
                >
                  <Search className="w-5 h-5" />
                </a>
                <button
                  className="arrow-button !hidden xl:!flex relative z-50"
                  aria-label="Поделиться"
                  onClick={toggleShareModal}
                >
                  <Share2 className="arrow-icon" />
                </button>
                <button
                  onClick={handleContactSupport}
                  className="rounded-xl glass-panel-interactive flex items-center justify-center glass-text-secondary cursor-pointer flex-shrink-0"
                  style={{ transition: 'none', width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}
                  title="Связь"
                >
                  <MessageCircle className="w-5 h-5" />
                </button>
              </div>
            </header>

            {/* Content Scroll Area */}
            <main className="flex-1 overflow-y-auto pb-8" style={{ paddingTop: '32px', paddingLeft: '40px', paddingRight: '32px' }}>
              <div className="max-w-6xl mx-auto space-y-8">

                {/* Рекламный блок перемещён в сайдбар (слева), чтобы не сдвигать основной контент */}


                {/* Hero Stats Cards — появляются с fadeSlideUp при монтировании */}
                {/* Grid: 3 колонки — левая карточка, правая колонка, реклама справа */}
                <div style={{ display: 'grid', gridTemplateColumns: '400px 400px 1fr', gap: '24px', alignItems: 'start' }}>
                  {/* Левая колонка: Общая подготовка + RuStore */}
                  <div className="flex flex-col w-full" style={{ gap: '24px' }}>
                    <div className="stat-card-animated glass-panel p-8 relative overflow-hidden" style={{ borderRadius: '24px', animationDelay: '0ms' }}>
                    <div className="flex justify-center items-start mb-8">
                      <div className="text-center">
                        <p className="glass-text-secondary font-medium text-lg">Общая подготовка</p>
                      </div>
                    </div>

                    {/* Двойная кольцевая диаграмма: Изучено (внешнее) + Усвоено (внутреннее) */}
                    <div style={{ minHeight: '320px' }}>
                      {(() => {
                        const totalAnswered = categoryStatsData.reduce((sum, item) => sum + item.answeredCount, 0);
                        const totalQuestions = categoryStatsData.reduce((sum, item) => sum + item.totalQuestions, 0);
                        const totalCoverageRate = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

                        if (totalAnswered === 0) {
                          return (
                            <div className="flex items-center justify-center h-full">
                              <p className="glass-text-secondary text-sm">Начните отвечать на вопросы</p>
                            </div>
                          );
                        }

                        // Общая функция для подготовки данных со всеми метриками
                        const getCommonData = (item: any) => ({
                          name: item.category.split(' ')[0].replace('.', ''),
                          fullName: item.category,
                          total: item.totalQuestions,
                          correct: item.correct,
                          answered: item.answeredCount,
                          coveragePct: item.totalQuestions > 0 ? Math.round((item.answeredCount / item.totalQuestions) * 100) : 0,
                          masteryPct: item.answeredCount > 0 ? Math.round((item.correct / item.answeredCount) * 100) : 0,
                          color: item.color,
                        });

                        // Данные для внешнего кольца: покрытие (% изученных вопросов от базы)
                        const outerData = categoryStatsData.map(item => ({
                          ...getCommonData(item),
                          value: item.answeredCount || 1, // Минимум 1, чтобы не было пустых
                        }));

                        // Данные для внутреннего кольца: усвоение (% правильных ответов)
                        const innerData = categoryStatsData.map(item => ({
                          ...getCommonData(item),
                          value: item.answeredCount || 1,
                        }));



                        return (
                          <div className="flex flex-col items-center gap-4">
                            {/* Диаграмма */}
                            <div className="relative" style={{ width: 300, height: 300 }}>
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ pointerEvents: 'none', zIndex: 0 }}>
                                <div className="flex flex-col items-center leading-none">
                                  <span className="text-2xl font-bold" style={{ color: '#10b981' }}>{totalSuccessRate}%</span>
                                  <span className="text-[10px] uppercase tracking-wider mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>Усвоено</span>
                                </div>
                                <div className="w-12 h-px bg-white/10"></div>
                                <div className="flex flex-col items-center leading-none">
                                  <span className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>{totalCoverageRate}%</span>
                                  <span className="text-[10px] uppercase tracking-wider mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Пройдено</span>
                                </div>
                              </div>

                              <ResponsiveContainer width="100%" height={300} style={{ zIndex: 10 }}>
                                <PieChart>
                                  {/* Внешнее кольцо: Изучено (покрытие базы) */}
                                  <Pie
                                    data={outerData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={120}
                                    innerRadius={90}
                                    paddingAngle={2}
                                    strokeWidth={0}
                                    animationBegin={0}
                                    animationDuration={800}
                                  >
                                    {outerData.map((entry, index) => (
                                      <Cell 
                                        key={`outer-${index}`} 
                                        fill={entry.color} 
                                        opacity={0.5 + (entry.coveragePct / 100) * 0.5}
                                        stroke="rgba(255,255,255,0.15)"
                                        strokeWidth={1}
                                      />
                                    ))}
                                  </Pie>

                                  {/* Внутреннее кольцо: Усвоено (правильные ответы) */}
                                  <Pie
                                    data={innerData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={82}
                                    innerRadius={55}
                                    paddingAngle={2}
                                    strokeWidth={0}
                                    animationBegin={300}
                                    animationDuration={800}
                                  >
                                    {innerData.map((entry, index) => (
                                      <Cell 
                                        key={`inner-${index}`} 
                                        fill={entry.color}
                                        opacity={0.3 + (entry.masteryPct / 100) * 0.7}
                                        stroke="rgba(255,255,255,0.1)"
                                        strokeWidth={1}
                                      />
                                    ))}
                                  </Pie>

                                  <RechartsTooltip
                                    content={({ active, payload }: any) => {
                                      if (active && payload && payload.length) {
                                        const data = payload[0].payload;
                                        const isCoverage = data.coveragePct !== undefined;
                                        return (
                                          <div style={{
                                            backgroundColor: 'rgb(15, 23, 42)',
                                            border: '1px solid rgba(255,255,255,0.15)',
                                            borderRadius: '12px',
                                            color: '#fff',
                                            padding: '12px 16px',
                                            fontSize: '12px',
                                            maxWidth: '220px',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                          }}>
                                            <p style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600, marginBottom: '8px', lineHeight: 1.2 }}>
                                              {data.fullName}
                                            </p>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                              <p style={{ color: '#e2e8f0', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: payload[0].color || '#fff' }}></span>
                                                <span>Пройдено: <b>{data.coveragePct}%</b> <span style={{opacity: 0.6, fontSize: '10px'}}>({data.answered}/{data.total})</span></span> 
                                              </p>
                                              <p style={{ color: '#e2e8f0', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: payload[0].color || '#fff', opacity: 0.6 }}></span>
                                                <span>Усвоено: <b>{data.masteryPct}%</b> <span style={{opacity: 0.6, fontSize: '10px'}}>({data.correct}/{data.answered})</span></span>
                                              </p>
                                            </div>
                                          </div>
                                        );
                                      }
                                      return null;
                                    }}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* --- RuStore --- */}
                    <div className="glass-panel p-5 flex flex-col justify-center relative overflow-hidden" style={{ borderRadius: '24px' }}>
                      <div className="flex flex-col items-center gap-3">
                        <img
                          src="/rustore-qr.png"
                          alt="RuStore QR Code"
                          className="w-20 h-20 rounded-lg"
                          style={{ objectFit: 'contain' }}
                        />
                        <a
                          href="https://www.rustore.ru/catalog/app/com.pp.app"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 transition-transform hover:scale-105"
                        >
                          <img
                            src="/rustore-logo.png"
                            alt="Скачайте из RuStore"
                            className="h-9"
                            style={{ objectFit: 'contain', maxWidth: '150px' }}
                          />
                          <ExternalLink className="w-3 h-3 glass-icon flex-shrink-0" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Side Stats Column */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {(() => {
                      // Используем общую статистику за весь период обучения
                      const totalCorrect = userStats.correctAnswers;
                      const totalIncorrect = userStats.incorrectAnswers;
                      const totalAnswers = totalCorrect + totalIncorrect;

                      // Вычисление данных для круговой диаграммы
                      const correctPercentage = totalAnswers > 0 ? (totalCorrect / totalAnswers) * 100 : 0;
                      const incorrectPercentage = totalAnswers > 0 ? (totalIncorrect / totalAnswers) * 100 : 0;

                      // Параметры для SVG круговой диаграммы (pie chart)
                      const size = 140;
                      const radius = 55;
                      const centerX = size / 2;
                      const centerY = size / 2;

                      // Функция для создания path сектора круга
                      const createArc = (startAngle: number, endAngle: number) => {
                        const start = {
                          x: centerX + radius * Math.cos((startAngle - 90) * (Math.PI / 180)),
                          y: centerY + radius * Math.sin((startAngle - 90) * (Math.PI / 180))
                        };
                        const end = {
                          x: centerX + radius * Math.cos((endAngle - 90) * (Math.PI / 180)),
                          y: centerY + radius * Math.sin((endAngle - 90) * (Math.PI / 180))
                        };
                        const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

                        return `M ${centerX} ${centerY} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
                      };

                      // Вычисление углов для секторов
                      const correctAngle = (correctPercentage / 100) * 360;
                      const incorrectAngle = (incorrectPercentage / 100) * 360;

                      return (
                        /* Правая верхняя карточка — ответы */
                        <div className="stat-card-animated glass-panel p-6 flex flex-col justify-between h-[calc(50%-12px)] relative overflow-hidden" style={{ borderRadius: '24px', animationDelay: '100ms' }}>
                          {totalAnswers > 0 ? (
                            <div className="flex flex-col items-center justify-center mt-4 gap-3">
                              <div className="relative">
                                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                                  {/* Правильные ответы (зеленый сектор) */}
                                  {correctPercentage > 0 && (
                                    <path
                                      d={createArc(0, correctAngle)}
                                      fill="#34d399"
                                      stroke="rgba(255, 255, 255, 0.2)"
                                      strokeWidth="1"
                                    />
                                  )}
                                  {/* Неправильные ответы (красный сектор) */}
                                  {incorrectPercentage > 0 && (
                                    <path
                                      d={createArc(correctAngle, correctAngle + incorrectAngle)}
                                      fill="#fb7185"
                                      stroke="rgba(255, 255, 255, 0.2)"
                                      strokeWidth="1"
                                    />
                                  )}
                                </svg>
                              </div>
                              <div className="flex flex-col gap-2 w-full">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4" style={{ color: '#34d399' }} />
                                    <span className="text-xs font-medium glass-text-secondary">Правильных</span>
                                  </div>
                                  <span className="text-xs font-bold glass-text-secondary">{totalCorrect} ({Math.round(correctPercentage)}%)</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <XCircle className="w-4 h-4" style={{ color: '#fb7185' }} />
                                    <span className="text-xs font-medium glass-text-secondary">Неправильных</span>
                                  </div>
                                  <span className="text-xs font-bold glass-text-secondary">{totalIncorrect} ({Math.round(incorrectPercentage)}%)</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full glass-progress-bg rounded-full h-1.5 mt-4 overflow-hidden">
                              <div className="glass-progress-fill h-full rounded-full transition-all duration-300" style={{ width: '75%', backgroundColor: '#34d399' }}></div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Нижняя правая карточка — время обучения */}
                    <div className="stat-card-animated glass-panel p-6 flex flex-col justify-center items-center h-[calc(50%-12px)] relative overflow-hidden" style={{ borderRadius: '24px', animationDelay: '200ms' }}>
                      <div className="text-center">
                        <p className="glass-text-secondary font-medium text-lg mb-3">Время обучения</p>
                        <div className="font-bold glass-text-secondary" style={{ fontSize: '3rem', lineHeight: '1' }}>{hours}ч {minutes}м</div>
                      </div>
                    </div>

                    {/* Блок магазинов приложений: Google Play */}
                    <div className="glass-panel p-5 flex flex-col justify-center relative overflow-hidden" style={{ borderRadius: '24px' }}>
                      <div className="flex items-center justify-center gap-3 w-full">
                        {/* --- Google Play --- */}
                        <div className="flex flex-col items-center gap-3 flex-1">
                          <img
                            src="/google-play-qr.png"
                            alt="Google Play QR Code"
                            className="w-20 h-20 rounded-lg"
                            style={{ objectFit: 'contain' }}
                          />
                          <a
                            href="https://play.google.com/store/apps/details?id=ru.vohr.pp&pcampaignid=web_share"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 transition-transform hover:scale-105"
                          >
                            <img
                              src="/google-play-logo.png"
                              alt="Доступно в Google Play"
                              className="h-9"
                              style={{ objectFit: 'contain', maxWidth: '150px' }}
                            />
                            <ExternalLink className="w-3 h-3 glass-icon flex-shrink-0" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Рекламная колонка — третья ячейка grid, всегда на одном уровне с карточками */}
                  <div style={{ position: 'sticky', top: 0 }}>
                    <div className="rounded-xl bg-white/5 w-full">
                      <YandexRTB type="adaptive" idMobile="R-A-17579918-10" idDesktop="R-A-17579918-11" />
                    </div>
                  </div>

                </div>

                {/* Bottom Section: Interrupted Test & Tips */}
                <div className="flex flex-wrap" style={{ gap: '24px' }}>
                  <div className="glass-panel p-6" style={{ borderRadius: '24px', width: '400px' }}>
                    <div className="mb-6 text-center">
                      <p className="glass-text-secondary font-medium text-lg">Текущая активность</p>
                    </div>

                    {testSession && !testSession.isCompleted ? (
                      <div
                        onClick={continueTest}
                        className="flex items-center p-4 glass-panel-interactive cursor-pointer"
                        style={{ transition: 'none', borderRadius: '16px' }}
                      >
                        <div className="flex-1">
                          <h4 className="glass-text-secondary font-medium">
                            {testSession.type === 'preparation' && 'Подготовка'}
                            {testSession.type === 'exam' && 'Экзамен'}
                            {testSession.type === 'marathon' && 'Марафон'}
                            {testSession.category && ` • ${testSession.category}`}
                          </h4>
                          <div className="flex items-center gap-3 mt-2">
                            <div className="flex-1 h-2 glass-progress-bg rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-300" style={{ width: `${Math.max(((testSession.currentQuestionIndex) / testSession.questions.length) * 100, 3)}%`, backgroundColor: '#34d399', minWidth: '3%' }}></div>
                            </div>
                            <span className="text-xs glass-text-secondary">{testSession.currentQuestionIndex} из {testSession.questions.length}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Пустое состояние — subtle pulse привлекает внимание без навязчивости */
                      <div className="empty-state-pulse text-center py-8 glass-text-secondary">
                        <p>Нет активных тестов. Начните новый!</p>
                      </div>
                    )}
                  </div>

                  {/* Daily Tip Widget */}
                  {currentTip && (
                    <div className="glass-panel p-6 relative overflow-hidden" style={{ borderRadius: '24px', width: '400px' }}>
                      <div>
                        <h2 className="font-bold glass-text-secondary text-lg mb-2">{currentTip.title}</h2>
                        <p className="text-sm glass-text-secondary mb-4">{currentTip.description}</p>
                        <div className="p-3 glass-panel-interactive" style={{ transition: 'none', borderRadius: '12px' }}>
                          <p className="text-xs glass-text-secondary"><span className="text-blue-300 font-medium">Польза:</span> {currentTip.benefit}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <SeoLandingSection onNavigate={onNavigate} />

                {/* Footer for Desktop */}
                <Footer onNavigate={onNavigate} />
              </div>
            </main>
          </div>
        </div>
      ) : (
        /* MOBILE LAYOUT (Original Layout, hidden on desktop) - показывается только на экранах < 1024px */
        <div className="flex flex-1 flex-col h-full relative" style={{ zIndex: 0 }}>

          {/* Background Shield Image for Mobile */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 -mt-[400px] xs:-mt-[100px] sm:-mt-[364px] md:-mt-[340px] lg:-mt-[100px] xl:-mt-[100px] 2xl:-mt-[100px]">
            <img
              src={shieldCheckImage640}
              srcSet={`${shieldCheckImage320} 320w, ${shieldCheckImage640} 640w, ${shieldCheckImage966} 966w`}
              sizes="(max-width: 640px) 320px, (max-width: 1024px) 480px, 640px"
              alt="Shield with checkmark"
              fetchPriority="high"
              decoding="async"
              width="640"
              height="640"
              className="w-80 h-80 xs:w-72 xs:h-72 sm:w-[22rem] sm:h-[22rem] md:w-[24rem] md:h-[24rem] lg:w-[28rem] lg:h-[28rem] xl:w-[30rem] xl:h-[30rem] 2xl:w-[32rem] 2xl:h-[32rem] landscape:w-72 landscape:h-72 landscape-compact:w-60 landscape-compact:h-60 select-none opacity-50 lg:opacity-30"
              style={{
                maxWidth: '90vw',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </div>

          {/* Fixed Header */}
          <div className="app-header-fixed relative z-10">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 sm:gap-3">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
                <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-xs landscape:text-sm font-semibold glass-text">
                  Ведомственная охрана
                </h1>
              </div>
              <div className="flex gap-2 sm:gap-3 md:gap-4 landscape:gap-1">
                <a href="/search"
                  onClick={(e) => { e.preventDefault(); onNavigate('search'); }}
                  className="arrow-button"
                  aria-label="Поиск"
                >
                  <Search className="arrow-icon" />
                </a>
                <a href="/settings"
                  onClick={(e) => { e.preventDefault(); onNavigate('settings'); }}
                  className="arrow-button"
                  aria-label="Настройки"
                >
                  <Settings className="arrow-icon" />
                </a>
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col px-4 xs-px-2 sm:px-6 md:px-8 landscape:px-3 landscape-compact-px-2 pb-6 sm:pb-8 md:pb-10 hide-scrollbar overflow-y-auto main-screen-content">
            {/* Interrupted Test Section */}
            <InterruptedTestCard />

            {!(testSession && !testSession.isCompleted) && (
              <p className="text-sm glass-text-secondary pt-3 pb-1">
                235 вопросов с ответами, бесплатно, без регистрации
              </p>
            )}

            {/* Main Action Buttons */}
            <div className="grid grid-cols-2 tablet-landscape-grid-cols-4 gap-4 sm:gap-6 md:gap-8 mb-4 sm:mb-6 md:mb-8 landscape-compact-space-y-1 landscape:gap-3">
              {/* Подготовка — primary action, чуть более заметный border */}
              <a href="/preparation"
                className="nav-btn-animated h-24 xs-h-20 sm:h-28 md:h-32 landscape:h-16 landscape-compact-h-12 landscape-extra-compact-h-8 landscape-compact-p-1 flex flex-col items-center justify-center gap-2 xs-gap-1 sm:gap-3 md:gap-3 landscape:gap-1 landscape-compact-gap-1 glass-panel-interactive rounded-2xl glass-text-secondary shadow-lg"
                style={{ animationDelay: '0ms', border: '1px solid rgba(255,255,255,0.25)' }}
                onClick={(e) => { e.preventDefault(); onNavigate('preparation'); }}
              >
                <Play className="h-7 w-7 xs-h-6 xs-w-6 sm:h-8 sm:w-8 md:h-9 md:w-9 landscape:h-6 landscape:w-6 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape-extra-compact-text-xs text-center leading-tight">Подготовка</span>
              </a>

              <a href="/exam"
                className="nav-btn-animated h-24 xs-h-20 sm:h-28 md:h-32 landscape:h-16 landscape-compact-h-12 landscape-extra-compact-h-8 landscape-compact-p-1 flex flex-col items-center justify-center gap-2 xs-gap-1 sm:gap-3 md:gap-3 landscape:gap-1 landscape-compact-gap-1 glass-panel-interactive rounded-2xl glass-text-secondary shadow-lg"
                style={{ animationDelay: '70ms' }}
                onClick={(e) => { e.preventDefault(); onNavigate('exam'); }}
              >
                <Trophy className="h-7 w-7 xs-h-6 xs-w-6 sm:h-8 sm:w-8 md:h-9 md:w-9 landscape:h-6 landscape:w-6 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape-extra-compact-text-xs text-center leading-tight">Экзамен</span>
              </a>

              <a href="/marathon"
                className="nav-btn-animated h-24 xs-h-20 sm:h-28 md:h-32 landscape:h-16 landscape-compact-h-12 landscape-extra-compact-h-8 landscape-compact-p-1 flex flex-col items-center justify-center gap-2 xs-gap-1 sm:gap-3 md:gap-3 landscape:gap-1 landscape-compact-gap-1 glass-panel-interactive rounded-2xl glass-text-secondary shadow-lg"
                style={{ animationDelay: '140ms' }}
                onClick={(e) => { e.preventDefault(); onNavigate('marathon'); }}
              >
                <Zap className="h-7 w-7 xs-h-6 xs-w-6 sm:h-8 sm:w-8 md:h-9 md:w-9 landscape:h-6 landscape:w-6 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape-extra-compact-text-xs text-center leading-tight">Марафон</span>
              </a>

              <a href="/favorites"
                className="nav-btn-animated h-24 xs-h-20 sm:h-28 md:h-32 landscape:h-16 landscape-compact-h-12 landscape-extra-compact-h-8 landscape-compact-p-1 flex flex-col items-center justify-center gap-2 xs-gap-1 sm:gap-3 md:gap-3 landscape:gap-1 landscape-compact-gap-1 glass-panel-interactive rounded-2xl glass-text-secondary shadow-lg"
                style={{ animationDelay: '210ms' }}
                onClick={(e) => { e.preventDefault(); onNavigate('favorites'); }}
              >
                <Star className="h-7 w-7 xs-h-6 xs-w-6 sm:h-8 sm:w-8 md:h-9 md:w-9 landscape:h-6 landscape:w-6 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape-extra-compact-text-xs text-center leading-tight">Избранное</span>
              </a>
            </div>

            {/* Statistics and Error Top Buttons */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-4 sm:mb-6 md:mb-8">
              <a href="/statistics"
                className="nav-btn-animated h-20 xs-h-18 sm:h-24 md:h-28 landscape:h-16 landscape-compact-h-12 landscape-extra-compact-h-8 landscape-compact-p-1 flex flex-col items-center justify-center gap-2 xs-gap-1 sm:gap-3 md:gap-3 landscape:gap-1 landscape-compact-gap-1 glass-panel-interactive rounded-2xl glass-text-secondary shadow-lg"
                style={{ animationDelay: '280ms' }}
                onClick={(e) => { e.preventDefault(); onNavigate('statistics'); }}
              >
                <BarChart3 className="h-6 w-6 xs-h-5 xs-w-5 sm:h-7 sm:w-7 md:h-8 md:w-8 landscape:h-6 landscape:w-6 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape-extra-compact-text-xs text-center leading-tight">Статистика</span>
              </a>

              <a href="/error-top"
                className="nav-btn-animated h-20 xs-h-18 sm:h-24 md:h-28 landscape:h-16 landscape-compact-h-12 landscape-extra-compact-h-8 landscape-compact-p-1 flex flex-col items-center justify-center gap-2 xs-gap-1 sm:gap-3 md:gap-3 landscape:gap-1 landscape-compact-gap-1 glass-panel-interactive rounded-2xl glass-text-secondary shadow-lg"
                style={{ animationDelay: '350ms' }}
                onClick={(e) => { e.preventDefault(); onNavigate('error-top'); }}
              >
                <TrendingDown className="h-6 w-6 xs-h-5 xs-w-5 sm:h-7 sm:w-7 md:h-8 md:w-8 landscape:h-6 landscape:w-6 text-rose-300 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-base landscape-compact-text-xs landscape-extra-compact-text-xs text-center leading-tight">Топ ошибок</span>
              </a>
            </div>

            {/* Additional Action Buttons */}
            <div className="mt-4 sm:mt-6 md:mt-8 grid grid-cols-2 gap-4 sm:gap-6 md:gap-8">
              <a href="/documents"
                className="h-16 xs-h-14 sm:h-18 md:h-20 landscape:h-16 landscape-compact:h-12 flex-col items-center justify-center gap-1 xs-gap-1 sm:gap-2 landscape:gap-1 landscape-compact:gap-1 glass-panel-interactive rounded-2xl glass-text-secondary px-3 shadow-lg"
                onClick={(e) => { e.preventDefault(); onNavigate('documents-catalog'); }}
              >
                <BookOpen className="h-5 w-5 xs-h-4 xs-w-4 sm:h-6 sm:w-6 md:h-7 md:w-7 landscape:h-5 landscape:w-5 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-sm landscape-compact:text-xs text-center leading-tight">
                  <span className="block sm:hidden text-xs">Полезная<br />информация</span>
                  <span className="hidden sm:block">Полезная информация</span>
                </span>
              </a>

              <a href="/useful-links"
                className="h-16 xs-h-14 sm:h-18 md:h-20 landscape:h-16 landscape-compact:h-12 flex flex-col items-center justify-center gap-1 xs-gap-1 sm:gap-2 landscape:gap-1 landscape-compact:gap-1 glass-interactive rounded-2xl glass-text px-3 shadow-lg"
                onClick={(e) => { e.preventDefault(); onNavigate('useful-links'); }}
              >
                <ExternalLink className="h-5 w-5 xs-h-4 xs-w-4 sm:h-6 sm:w-6 md:h-7 md:w-7 landscape:h-5 landscape:w-5 glass-icon" />
                <span className="font-semibold text-xs xs-text-xs sm:text-sm md:text-sm landscape-compact:text-xs text-center leading-tight">
                  <span className="block sm:hidden text-xs">Полезные<br />ссылки</span>
                  <span className="hidden sm:block">Полезные ссылки</span>
                </span>
              </a>
            </div>

            {/* Action Buttons Section */}
            <div
              className="mt-4 sm:mt-6 md:mt-8 grid grid-cols-3 gap-3 sm:gap-4 md:gap-6"
              style={{ position: 'relative', zIndex: 100 }}
            >
              <button
                onClick={handleContactSupport}
                className="h-12 xs-h-10 sm:h-14 md:h-16 landscape:h-12 landscape-compact:h-10 flex items-center justify-center glass-panel-interactive rounded-xl xs-rounded-lg sm:rounded-2xl glass-text-secondary shadow-lg px-1 cursor-pointer"
                style={{ pointerEvents: 'auto', zIndex: 100, position: 'relative' }}
                title="Связь"
                type="button"
              >
                <MessageCircle className="h-4 w-4 xs-h-3 xs-w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 landscape:h-4 landscape:w-4 glass-icon" />
              </button>

              <button
                onClick={toggleShareModal}
                className="h-12 xs-h-10 sm:h-14 md:h-16 landscape:h-12 landscape-compact:h-10 flex items-center justify-center glass-panel-interactive rounded-xl xs-rounded-lg sm:rounded-2xl glass-text-secondary shadow-lg px-1 cursor-pointer"
                style={{ pointerEvents: 'auto', zIndex: 100, position: 'relative' }}
                title="Поделиться"
                type="button"
              >
                <Share2 className="h-4 w-4 xs-h-3 xs-w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 landscape:h-4 landscape:w-4 glass-icon" />
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRate();
                }}
                className="h-12 xs-h-10 sm:h-14 md:h-16 landscape:h-12 landscape-compact:h-10 flex items-center justify-center glass-panel-interactive rounded-xl xs-rounded-lg sm:rounded-2xl glass-text-secondary shadow-lg px-1 cursor-pointer"
                style={{ pointerEvents: 'auto', zIndex: 100, position: 'relative' }}
                title="Оценка"
                type="button"
              >
                <ThumbsUp className="h-4 w-4 xs-h-3 xs-w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 landscape:h-4 landscape:w-4 glass-icon" />
              </button>
            </div>

            <div className="mt-6 sm:mt-8 md:mt-10">
              <TipCard />
              <SeoLandingSection onNavigate={onNavigate} />
            </div>

            {/* Footer */}
            <Footer onNavigate={onNavigate} />
          </div>
        </div>
      )}

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-md sm:max-w-lg mx-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg md:text-xl lg:text-2xl">Связаться с нами</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <button
              onClick={handleContactTelegram}
              className="w-full px-6 py-4 text-left glass-panel rounded-xl glass-panel-interactive flex items-center gap-4 hover:bg-white/5 transition-colors"
            >
              <MessageCircle className="w-6 h-6 glass-icon flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="glass-text-secondary text-base font-medium mb-1 flex items-center gap-1">
                  Telegram <ExternalLink className="w-3 h-3" />
                </div>
                <div className="glass-text-secondary text-sm truncate">@BOXP_AIbot</div>
              </div>
            </button>
            <button
              onClick={handleContactEmail}
              className="w-full px-6 py-4 text-left glass-panel rounded-xl glass-panel-interactive flex items-center gap-4 hover:bg-white/5 transition-colors"
            >
              <Mail className="w-6 h-6 glass-icon flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="glass-text-secondary text-base font-medium mb-1 flex items-center gap-1">
                  Email <ExternalLink className="w-3 h-3" />
                </div>
                <div className="glass-text-secondary text-sm truncate">bumer.bmw@gmail.com</div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />
    </div>
  );
};
